import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  TextField,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { PersonalPermissionsSection } from './PersonalPermissionsSection';
import { api } from '@/lib/api';
import { useNotify } from '@/stores/notificationStore';
import { useRoles } from '@/hooks/useRoles';
import {
  roleDefinitionLabel,
  resolveMemberRoleId,
  type TeamMember,
} from './staffRoleLabels';

interface EditTeamMemberDialogProps {
  open: boolean;
  member: TeamMember | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditTeamMemberDialog({ open, member, onClose, onSuccess }: EditTeamMemberDialogProps) {
  const { t } = useTranslation('settings');
  const notify = useNotify();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [roleId, setRoleId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const { data: roleDefinitions, isLoading: rolesLoading, isError: rolesError } = useRoles();
  const assignableRoles = (roleDefinitions ?? []).filter((role) => role.isActive);

  useEffect(() => {
    if (member) {
      setFirstName(member.firstName);
      setLastName(member.lastName);
      setEmail(member.email);
      setPhone(member.phone || '');
      setError(null);
      setIsDirty(false);
    }
  }, [member]);

  useEffect(() => {
    if (!member) return;
    const active = (roleDefinitions ?? []).filter((role) => role.isActive);
    if (active.length === 0) return;
    setRoleId(resolveMemberRoleId(member, active));
  }, [member, roleDefinitions]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!member) return;
    setError(null);

    if (!firstName || !lastName || !email || !roleId) {
      setError(t('addMemberRequiredFields'));
      return;
    }

    setSaving(true);
    try {
      await api.patch(`/users/${member.id}`, {
        firstName,
        lastName,
        email,
        phone: phone || null,
        roleId,
      });
      notify.success(t('memberUpdated'));
      onSuccess();
      onClose();
    } catch {
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={t('editMember')}
      isDirty={isDirty}
      onSubmit={() => void handleSave()}
      isLoading={saving}
      submitDisabled={!roleId || rolesLoading}
      submitLabel={t('saveChanges')}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {rolesError && <Alert severity="error">{t('rolesLoadFailed')}</Alert>}
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('firstName')}
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); setIsDirty(true); }}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label={t('lastName')}
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); setIsDirty(true); }}
              required
              fullWidth
            />
          </Stack>
          <TextField
            label={t('email')}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setIsDirty(true); }}
            required
          />
          <TextField
            label={t('phone')}
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setIsDirty(true); }}
          />
          <TextField
            label={t('role')}
            select
            value={roleId}
            onChange={(e) => { setRoleId(e.target.value); setIsDirty(true); }}
            required
            disabled={rolesLoading || assignableRoles.length === 0}
          >
            {assignableRoles.map((role) => (
              <MenuItem key={role.id} value={role.id}>
                {roleDefinitionLabel(role, t)}
              </MenuItem>
            ))}
          </TextField>
          {member && <PersonalPermissionsSection userId={member.id} />}
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
